import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@app/constants';
import { IPrivileges } from '@app/models/user-privileges.model';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';

import { UnityDirectStateService } from './unity-direct-state.service';

@Injectable({ providedIn: 'root' })
export class AppUserContextService {
  private _userRoles: string[];
  private token: string;
  private bookingRead: boolean;
  private bookingModify: boolean;
  private billingRead: boolean;
  private billingModify: boolean;
  private rateRead: boolean;

  private adminClientApp: boolean; // Remove for future ADMIN features

  constructor(
    private readonly http: HttpClient,
    private readonly udStateService: UnityDirectStateService,
    private readonly logger: NGXLogger
  ) {}

  set userRoles(roles: string[]) {
    if (!roles?.length) {
      this._userRoles = [];
      this.udStateService.isAdminUser = false;
      return;
    }
    this._userRoles = [...roles];
    this.checkAdminUser();
  }

  get userRoles(): string[] {
    return this._userRoles;
  }

  private checkAdminUser(): void {
    const admin = 'admin';
    const isAdminRoleFound = this._userRoles?.some((role: string) =>
      role.toLowerCase().includes(admin.toLowerCase())
    );
    this.udStateService.isAdminUser = !!isAdminRoleFound;
  }

  /**
   * Receives the token, stores it and calls decode
   * @param token {string}
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * Sets User Privileges from UnityPrivileges response
   * @param privileges {IPrivileges}
   */
  setPrivileges(privileges: IPrivileges): void {
    this.bookingRead = privileges.book.read;
    this.bookingModify = privileges.book.modify;
    this.billingRead = privileges.billing.read;
    this.billingModify = privileges.billing.modify;
    this.rateRead = privileges.rate.read;

    // Remove for future ADMIN features
    this.adminClientApp =
      privileges.clientApplication.modify && privileges.clientApplication.read;

    this.setUserAccess();
  }

  /**
   * Gets User Privileges
   * @returns {IPrivileges}
   */
  public getPrivileges(): Observable<IPrivileges> {
    return this.http.get<IPrivileges>(
      `${Constants.appConfiguration.apiRoot}/UnityPrivileges`
    );
  }

  /**
   * Sets the values from Pivileges response and checks if User has access to New quote, Booking or Billing
   */
  setUserAccess(): void {
    this.udStateService.canUserAccessNewQuote =
      this.rateRead || this.bookingModify || this.bookingRead;
    this.udStateService.canUserModifyBooking = this.bookingModify;
    this.udStateService.canUserModifyBilling = this.billingModify;
    this.udStateService.canUserAccessBilling =
      this.billingModify || this.billingRead;
    this.udStateService.canUserAccessAndModifyAdminClientApp =
      this.adminClientApp;

    this.logger.info(
      '\nACCESS - New Quote: ',
      this.udStateService.canUserAccessNewQuote ? '✔️' : '❌'
    );
    this.logger.info(
      '\nACCESS - Modify Booking: ',
      this.udStateService.canUserModifyBooking ? '✔️' : '❌'
    );
    this.logger.info(
      '\nACCESS - Read Billing: ',
      this.udStateService.canUserAccessBilling ? '✔️' : '❌'
    );
    this.logger.info(
      '\nACCESS - Modify Billing: ',
      this.udStateService.canUserModifyBilling ? '✔️' : '❌'
    );
  }
}
